import React, { useState } from 'react';
import './SourceCitation.css';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import DocumentViewer from './DocumentViewer';
import ChunkImage from './ChunkImage';

const SourceCitation = ({ source, index, sourceRefs, expandToCitation, onExpansionComplete }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedPages, setExpandedPages] = useState(new Set());
  const [isViewerOpen, setIsViewerOpen] = useState(false);

  // Effect to auto-expand when expandToCitation matches a citation in this source
  React.useEffect(() => {
    if (expandToCitation) {
      // Check if any page in this source has the target citation ID
      // Look in both citationId (primary) and allCitationIds (for merged duplicates)
      const pageIdx = source.pages?.findIndex(p =>
        p.citationId === expandToCitation ||
        (p.allCitationIds && p.allCitationIds.includes(expandToCitation))
      );
      if (pageIdx !== -1) {
        // This source contains the target citation - expand it
        setIsExpanded(true);
        setExpandedPages(prev => new Set([...prev, pageIdx]));

        // After expansion, notify parent and scroll to the element
        // Use setTimeout to allow DOM to update
        setTimeout(() => {
          const refKey = `${index}-${pageIdx}`;
          if (sourceRefs?.current?.[refKey]) {
            const element = sourceRefs.current[refKey];
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.classList.add('citation-highlight');
            setTimeout(() => {
              element.classList.remove('citation-highlight');
            }, 2000);
          }
          if (onExpansionComplete) {
            onExpansionComplete();
          }
        }, 100);
      }
    }
  }, [expandToCitation, source.pages, index, sourceRefs, onExpansionComplete]);

  // Helper to set ref for a specific page/chunk for citation scrolling
  const setPageRef = (pageIdx, el) => {
    if (sourceRefs && sourceRefs.current) {
      const refKey = `${index}-${pageIdx}`;
      sourceRefs.current[refKey] = el;
      if (el) {
        console.log('Registered source ref:', refKey, 'for citation:', source.pages?.[pageIdx]?.citationId);
      }
    }
  };

  const toggleExpand = (e) => {
    // Don't toggle if clicking specific actions or interactive author elements
    if (e.target.closest('.source-actions') || e.target.closest('.author-interactive')) return;

    setIsExpanded(!isExpanded);
    if (e && e.currentTarget) {
      const element = e.currentTarget;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          element.blur();
        });
      });
    }
  };

  const togglePageExpand = (pageIdx, e) => {
    e.stopPropagation();
    const newExpandedPages = new Set(expandedPages);
    if (newExpandedPages.has(pageIdx)) {
      newExpandedPages.delete(pageIdx);
    } else {
      newExpandedPages.add(pageIdx);
    }
    setExpandedPages(newExpandedPages);
    if (e && e.currentTarget) {
      const element = e.currentTarget;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          element.blur();
        });
      });
    }
  };

  const openDocumentViewer = (e) => {
    e.stopPropagation();
    setIsViewerOpen(true);
  };

  // Interactive Author List Component
  const AuthorList = ({ authorString }) => {
    const [expanded, setExpanded] = useState(false);
    
    if (!authorString) return null;

    const authors = authorString.split(/[,;]|\s+and\s+/i).map(author => author.trim());
    const formattedAuthors = authors.map(author => {
      const nameParts = author.trim().split(/\s+/);
      if (nameParts.length === 1) return author;
      const initials = nameParts.slice(0, -1).map(part => {
        if (part.replace('.', '').length === 1) {
          return part.endsWith('.') ? part : part + '.';
        }
        return part.charAt(0).toUpperCase() + '.';
      });
      const lastName = nameParts[nameParts.length - 1];
      return [...initials, lastName].join(' ');
    });

    if (formattedAuthors.length < 4) {
      return <span>{formattedAuthors.join(', ')}</span>;
    }

    if (expanded) {
      return (
        <span>
          {formattedAuthors.join(', ')}
          <span 
            onClick={(e) => { e.stopPropagation(); setExpanded(false); }}
            className="text-kaleido-purple cursor-pointer hover:underline ml-1 text-xs author-interactive"
            role="button"
          >
            (show less)
          </span>
        </span>
      );
    }

    const visibleAuthors = formattedAuthors.slice(0, 3).join(', ');
    const hiddenAuthors = formattedAuthors.slice(3).join(', ');

    return (
      <span>
        {visibleAuthors}
        <span>, </span>
        <span 
          className="
            inline-flex items-center justify-center
            bg-kaleido-purple/10 text-kaleido-purple 
            px-1.5 py-px rounded-md text-[10px] font-semibold tracking-wide
            cursor-pointer transition-colors duration-150
            hover:bg-kaleido-purple/20
            relative group align-baseline ml-0.5 -mb-px author-interactive
          "
          onClick={(e) => { e.stopPropagation(); setExpanded(true); }}
          title={hiddenAuthors}
        >
          <span className="italic">et al.</span>
          {/* Tooltip for hover */}
          <span className="
            absolute bottom-full left-1/2 -translate-x-1/2 mb-1 
            hidden group-hover:block 
            bg-gray-800 text-white text-xs rounded px-2 py-1 
            whitespace-nowrap z-50
            pointer-events-none
          ">
            {hiddenAuthors}
          </span>
        </span>
      </span>
    );
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleExpand();
    }
  };

  // Preprocess markdown to enhance table formatting
  const preprocessMarkdown = (text) => {
    if (!text) return '';
    let enhanced = text;
    // Bold column headers that end with colon
    enhanced = enhanced.replace(/^([A-Z][A-Za-z\s,&]+:)$/gm, '**$1**');
    enhanced = enhanced.replace(/\n([A-Z][A-Za-z\s,&]+:)\n/g, '\n**$1**\n');
    enhanced = enhanced.replace(/^([A-Z][A-Za-z\s,&]+:)\s+/gm, '**$1** ');
    enhanced = enhanced.replace(/\n([A-Z][A-Za-z\s,&]+:)\s+/g, '\n**$1** ');
    enhanced = enhanced.replace(/\|\s*([A-Z][A-Za-z\s,&]+:)\s+/g, '| **$1** ');
    return enhanced;
  };

  const formatPagesSummary = () => {
    if (!source.pages || source.pages.length === 0) return null;

    // Collect all page numbers from both pageNumber (singular) and pageNumbers (array)
    const allPageNumbers = [];
    source.pages.forEach(p => {
      // Check for pageNumbers array first (may contain multiple pages)
      if (p.pageNumbers && Array.isArray(p.pageNumbers)) {
        allPageNumbers.push(...p.pageNumbers);
      } else if (p.pageNumber != null) {
        // Fall back to single pageNumber
        allPageNumbers.push(p.pageNumber);
      }
    });

    if (allPageNumbers.length === 0) return null;
    // Convert 0-indexed to 1-indexed for display
    const displayPages = allPageNumbers.map(p => p + 1);
    const sortedPages = [...new Set(displayPages)].sort((a, b) => a - b); // Deduplicate and sort
    if (sortedPages.length === 1) return `p. ${sortedPages[0]}`;

    // Group consecutive pages into ranges (e.g., [1,2,3,5,6,184,185] -> "1-3, 5-6, 184-185")
    const ranges = [];
    let rangeStart = sortedPages[0];
    let rangeEnd = sortedPages[0];

    for (let i = 1; i < sortedPages.length; i++) {
      if (sortedPages[i] === rangeEnd + 1) {
        rangeEnd = sortedPages[i];
      } else {
        ranges.push(rangeStart === rangeEnd ? `${rangeStart}` : `${rangeStart}-${rangeEnd}`);
        rangeStart = sortedPages[i];
        rangeEnd = sortedPages[i];
      }
    }
    ranges.push(rangeStart === rangeEnd ? `${rangeStart}` : `${rangeStart}-${rangeEnd}`);

    return ranges.length === 1 && ranges[0].includes('-')
      ? `pp. ${ranges[0]}`
      : `pp. ${ranges.join(', ')}`;
  };

  const pagesSummary = formatPagesSummary();

  // Collect all citation IDs that reference this document (for display in header)
  const getDocumentCitationIds = () => {
    if (!source.pages || source.pages.length === 0) return [];
    const allIds = new Set();
    source.pages.forEach(p => {
      if (p.citationId) allIds.add(p.citationId);
      if (p.allCitationIds) p.allCitationIds.forEach(id => allIds.add(id));
    });
    return [...allIds].sort((a, b) => a - b);
  };
  const documentCitationIds = getDocumentCitationIds();

  // Get the source filename for image loading
  const sourceFilename = source.fullMetadata?.source || '';

  // Custom components for react-markdown
  const markdownComponents = {
    // Custom image renderer that loads images on-demand via ChunkImage
    img: ({ node, src, alt, ...props }) => {
      // Check if this is a relative image reference (like img-0.jpeg)
      // These need to be loaded via the chunk image endpoint
      if (src && !src.startsWith('http') && !src.startsWith('data:')) {
        return (
          <ChunkImage
            imageId={src}
            source={sourceFilename}
            alt={alt}
          />
        );
      }
      // For absolute URLs or data URIs, render normally
      return (
        <div className="chunk-image-container">
          <img src={src} alt={alt || 'Document image'} className="chunk-image" loading="lazy" {...props} />
        </div>
      );
    },
    // Wrap tables for horizontal scrolling
    table: ({ node, children, ...props }) => (
      <div className="table-responsive">
        <table {...props}>{children}</table>
      </div>
    ),
  };

  // Render page content with react-markdown
  const renderPageContent = (content) => {
    if (!content) return null;
    const preprocessed = preprocessMarkdown(content);
    return (
      <Markdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[rehypeKatex]}
        components={markdownComponents}
      >
        {preprocessed}
      </Markdown>
    );
  };

  return (
    <>
      <div className={`source-citation-item ${isExpanded ? 'expanded' : ''}`}>
        {/* Document Header - Always visible */}
        <div
          className="source-citation-header"
          onClick={toggleExpand}
          onKeyPress={handleKeyPress}
          tabIndex={0}
          role="button"
          aria-expanded={isExpanded}
        >
          <span className="source-number">
            {documentCitationIds.length > 0 
              ? `[${documentCitationIds.join(', ')}]`
              : `[${index + 1}]`
            }
          </span>
          <div className="source-title-block">
            <span className="source-title">{source.displayName}</span>
            {(source.authors || source.citationSubtitle) && (
              <span className="source-subtitle">
                {source.authors && <AuthorList authorString={source.authors} />}
                {source.authors && source.citationSubtitle && <span> • </span>}
                {source.citationSubtitle && <span>{source.citationSubtitle}</span>}
              </span>
            )}
          </div>
          {pagesSummary && (
            <span className="source-page" aria-label={pagesSummary}>
              {pagesSummary}
            </span>
          )}
          <span className="expand-icon" aria-hidden="true">
            {isExpanded ? '▼' : '▶'}
          </span>
        </div>

        {/* Expandable Document Details */}
        {isExpanded && (
          <div className="source-detail">
            {/* Document-level metadata with View Source Action */}
            <div className="source-meta">
              <div className="meta-content">
                {source.fullMetadata.document_reference && (
                  <div>
                    <strong>Reference:</strong>{' '}
                    <Markdown
                      remarkPlugins={[remarkMath]}
                      rehypePlugins={[rehypeKatex]}
                      components={{ p: ({ children }) => <span>{children}</span> }}
                    >
                      {source.fullMetadata.document_reference}
                    </Markdown>
                  </div>
                )}
                {source.fullMetadata.source && (
                  <div><strong>File:</strong> {source.fullMetadata.source}</div>
                )}
                {source.fullMetadata.country_of_origin && (
                  <div><strong>Country:</strong> {source.fullMetadata.country_of_origin}</div>
                )}
              </div>

              {/* View Full Document Button */}
              <div className="source-actions">
                <button
                  className="view-source-btn"
                  onClick={openDocumentViewer}
                  title="Read full document"
                >
                  <span className="btn-icon">📖</span>
                  View Full Document
                </button>
              </div>
            </div>

            {/* Pages Section */}
            {source.pages && source.pages.length > 0 && (
              <div className="pages-container">
                {source.pages.map((pageData, pageIdx) => {
                  const isPageExpanded = expandedPages.has(pageIdx);
                  const formatPageNumber = () => {
                    if (pageData.pageNumbers && Array.isArray(pageData.pageNumbers) && pageData.pageNumbers.length > 1) {
                      // Convert 0-indexed to 1-indexed for display
                      const displayPages = pageData.pageNumbers.map(p => p + 1);
                      const sortedPages = [...displayPages].sort((a, b) => a - b);

                      // Group consecutive pages into ranges (e.g., [1,2,3,5,6,184,185] -> "1-3, 5-6, 184-185")
                      const ranges = [];
                      let rangeStart = sortedPages[0];
                      let rangeEnd = sortedPages[0];

                      for (let i = 1; i < sortedPages.length; i++) {
                        if (sortedPages[i] === rangeEnd + 1) {
                          // Continue the current range
                          rangeEnd = sortedPages[i];
                        } else {
                          // End current range and start a new one
                          ranges.push(rangeStart === rangeEnd ? `${rangeStart}` : `${rangeStart}-${rangeEnd}`);
                          rangeStart = sortedPages[i];
                          rangeEnd = sortedPages[i];
                        }
                      }
                      // Add the last range
                      ranges.push(rangeStart === rangeEnd ? `${rangeStart}` : `${rangeStart}-${rangeEnd}`);

                      return ranges.length === 1 && ranges[0].includes('-')
                        ? `pp. ${ranges[0]}`
                        : `pp. ${ranges.join(', ')}`;
                    } else if (pageData.pageNumber != null) {
                      // Convert 0-indexed to 1-indexed
                      return `p. ${pageData.pageNumber + 1}`;
                    }
                    return 'Page';
                  };

                  return (
                    <div
                      key={pageIdx}
                      className="page-section"
                      ref={(el) => setPageRef(pageIdx, el)}
                      data-citation-id={pageData.citationId}
                    >
                      <div
                        className="page-header"
                        onClick={(e) => togglePageExpand(pageIdx, e)}
                        role="button"
                        tabIndex={0}
                        aria-expanded={isPageExpanded}
                      >
                        <span className="page-label">
                          {/* Show all citation IDs if multiple citations point to this chunk */}
                          {pageData.allCitationIds && pageData.allCitationIds.length > 1 ? (
                            pageData.allCitationIds.map((cId, idx) => (
                              <span
                                key={cId}
                                className="citation-badge"
                                title={`Citation ${cId}`}
                              >
                                {cId}
                              </span>
                            ))
                          ) : pageData.citationId ? (
                            <span className="citation-badge" title={`Citation ${pageData.citationId}`}>
                              {pageData.citationId}
                            </span>
                          ) : null}
                          {formatPageNumber()}
                        </span>
                        <span className="page-expand-icon" aria-hidden="true">
                          {isPageExpanded ? '▼' : '▶'}
                        </span>
                      </div>

                      {isPageExpanded && pageData.content && (
                        <div className="page-content">
                          <div className="source-text-content">
                            {renderPageContent(pageData.content)}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal Viewer */}
      <DocumentViewer
        isOpen={isViewerOpen}
        onClose={() => setIsViewerOpen(false)}
        sourceMetadata={source.fullMetadata}
      />
    </>
  );
};

export default SourceCitation;
